import React, { useEffect, useMemo, useState } from "react";
import "./HelpVideoPopup.css";
import { useDispatch, useSelector } from "react-redux";
import { Typography } from "../../design_system/StyledComponents/components/Typography";
import useResources from "../../hooks/useResources";
import ReactPlayer from "react-player/lazy";
import { restoreLinesAndPoints, setHelpVideo, setImageOpacity, setShowInstructionPopup, toggleTutorial } from "../../Actions/ApplicationStateAction";
import { Button } from "../../design_system/StyledComponents/components/Button";
import { Check, Close } from "@mui/icons-material";
import instructions from "../../assets/instructions.svg"
import book from "../../assets/book_3.svg"
import { setTypeId } from "../../Actions/DrawingActions";
import { setSelectedButton } from "../../features/drawing/drwingSlice";
import useModes from "../../hooks/useModes";
import { Canvas } from "react-three-fiber";
import { OrbitControls, OrthographicCamera } from "@react-three/drei";
import WallGeometry from "../Geometry/WallGeometry";

const videoData = {
  addRoom: {
    title: "How to add a room",
    subtitle: "to understand how adding a room works, watch this video",
    videoUrl: "/videos/drawTool/addNewRoom.mov",
  },
  divideRoom: {
    title: "How to divide a room",
    subtitle: "to understand how dividing a room works, watch this video",
    videoUrl: "/videos/drawTool/divideARoom.mov",
  },
  setScale: {
    title: "How to set scale",
    subtitle: "to understand how setting scale works, watch this video",
    videoUrl: "/videos/drawTool/setTheScale.mov",
  },
  addWall: {
    title: "How to add a wall",
    subtitle: "to understand how adding a wall works, watch this video",
    videoUrl: "/videos/drawTool/addNewWall.mov",
  },
  defineRoom: {
    title: "How to define a room",
    subtitle: "to understand how to define a room, watch this video",
    videoUrl: "/videos/drawTool/defineRoom.mov",
  }
};

const HelpVideoPopup = () => {
  const { helpVideo, helpVideoType, showInstructionPopup, designStep, backupLines, selectionMode, storeLines } = useSelector(
    (state) => state.ApplicationState
  );
  const dispatch = useDispatch();
  const { getRomeCdnUrl } = useResources();
  const { toggleSelectionMode } = useModes();

  const [slideIn, setSlideIn] = useState(false);

  useEffect(() => {
    setSlideIn(!!helpVideo);
  }, [helpVideo]);

  const videoPlayer = useMemo(() => {
    if (!helpVideo || !helpVideoType || helpVideoType === "instruction" || helpVideoType === "tutorial" || helpVideoType === "finishTutorial") return null;

    return (
      <ReactPlayer
        playsinline
        playing={true}
        muted
        controls
        loop
        pip
        className="help-popup-video"
        url={getRomeCdnUrl() + videoData[helpVideoType].videoUrl}
      />
    );
  }, [helpVideoType]); // Add all necessary dependencies here

  const handleClose = () => {
    setSlideIn(false);
    setTimeout(() => {
      dispatch(setHelpVideo(false));
      if (showInstructionPopup && helpVideoType === "instruction") {
        setTimeout(() => {
          dispatch(setShowInstructionPopup(false))
          var type = "";
          if (designStep === 1) {
            type = "setScale";
          } else if (designStep === 2) {
            // type = "addWall";
          } else if (designStep === 3) {
            // type = "defineRoom";
          }
          dispatch(setHelpVideo(true, type));
        }, 600)
      }
      if (showInstructionPopup && helpVideoType === "finishTutorial") {
        dispatch(toggleTutorial(false))
        dispatch(setTypeId(0))
        dispatch(setImageOpacity(0.4))
        dispatch(setSelectedButton(null))
        if (!selectionMode) {
          toggleSelectionMode();
        }
        dispatch(restoreLinesAndPoints())
      }
    }, 600)
    window.GAEvent("DrawTool", "ButtonClicked", "CloseHelpVideo", helpVideoType);
  };

  if (!helpVideo || !helpVideoType) return null;

  return (
    <div className="help-popup-wrapper" onClick={handleClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={
          slideIn ? "help-popup-container" : "help-popup-container-hidden"
        }
        style={{
          width: helpVideoType === "instruction" ? "min(90%, 1100px )" : helpVideoType === "tutorial" ? "min(90%, 510px )" : helpVideoType === "finishTutorial" ? "min(90%, 760px )" : "",
        }}
      >
        <div className="help-popup-close-icon" onClick={handleClose}>
          <Close />
        </div>
        {
          helpVideoType === "tutorial" ? (
            <div className="help-popup-tutorial">
              <img style={{ height: "80px", width: "80px" }} src={book} alt="instructions" />
              <Typography modifiers={["header4", "medium"]}>Learn how to draw</Typography>
              <Typography modifiers={["body", "black550"]}>Start with a quick tutorial to learn the basics of drawing a plan before creating your own.</Typography>
              <Button style={{ height: "44px", width: "100%" }} onClick={handleClose} modifiers={["black"]}>Start Tutorial</Button>
            </div>
          )
            : helpVideoType === "instruction" ? (
              <div className="help-popup-instruction">
                <Typography modifiers={["header4", "bold"]}><Typography modifiers={["inline", "primary"]}>2 easy</Typography> steps to get your 3D home</Typography>
                <img src={instructions} alt="instructions" />
                <div className="help-popup-button-wrapper">
                  <Button style={{ height: "44px" }} onClick={handleClose} modifiers={["black"]}>Start</Button>
                </div>
              </div>
            )
              : helpVideoType === "finishTutorial" ? (
                <div className="help-popup-tutorial">
                  <div style={{ width: "112px", height: "112px", backgroundColor: "#509F9B", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Check style={{ color: "white", fontSize: "88px" }} />
                  </div>
                  <Typography style={{ color: "#509F9B", marginBottom: "-8px" }} modifiers={["header4", "bold"]}>Tutorial Completed!</Typography>
                  <Typography style={{ color: "#6E757A" }} modifiers={["subtitle1", "black550"]}>Here is how your drawing will look like in 3D</Typography>
                  <img 
                    src={getRomeCdnUrl() + "/Images/DrawTool/3DRoom.jpeg"}
                    alt="3D Room Preview"
                    style={{
                      width: "100%",
                      aspectRatio: "5/2",
                      backgroundColor: "#EFEFEF",
                      borderRadius: "16px",
                      objectFit: "cover"
                    }}
                    />
                  {/* <Canvas
                    style={{
                      width: "100%",
                      aspectRatio: "5/2",
                      backgroundColor: "#EFEFEF",
                      borderRadius: "16px",
                    }}
                    orthographic
                    camera={{ position: [300, 300, 500], fov: 75 }} // Move camera to a diagonal position
                  >
                    <OrthographicCamera
                      makeDefault
                      zoom={0.55}
                      position={[300, 300, 500]} // Move it up and forward
                      rotation={[-Math.PI / 6, 0, 0]} // Tilt down slightly (negative X rotation)
                    />

                    {storeLines.map((line) => (
                      <WallGeometry
                        key={line.id}
                        start={line.points[0]}
                        end={line.points[1]}
                        dimension={{ width: line.width, height: line.height }}
                        widthchange={line.widthchange}
                        widthchangetype={line.widthchangetype}
                        type={line.type}
                        typeId={line.typeId}
                        isSelected={false}
                      />
                    ))}

                    <OrbitControls
                      enablePan={true}
                      zoomToCursor={true}
                      maxAzimuthAngle={Math.PI / 3}
                      minAzimuthAngle={-Math.PI / 3}
                      defaultPolarAngle={0} // Tilted default view
                    />
                  </Canvas> */}
                  
                  <hr style={{ width: "100%", border: "1px solid #ECECEC" }} />
                  <Button style={{ height: "44px", width: "100%" }} onClick={handleClose} modifiers={["black"]}>Start drawing my floorplan</Button>
                </div>
              )
                :
                <>

                  {videoPlayer}
                  <Typography modifiers={["black600", "body", "bold"]}>
                    {videoData[helpVideoType].title}
                  </Typography>
                  <Typography modifiers={["subtitle2", "medium"]} style={{ color: "#6E757A" }}>
                    {videoData[helpVideoType].subtitle}
                  </Typography>
                  <div className="help-popup-button-wrapper">
                    <Button onClick={handleClose} modifiers={["black"]}>Okay</Button>
                  </div>
                </>
        }
      </div>
    </div>
  );
};

export default HelpVideoPopup;
